import React, { useMemo, useState, useEffect } from 'react';
import {
  useChatInteract,
  useChatData,
  useAuth,
  useChatMessages,
} from '@chainlit/react-client';
import { chatSettingsValueState } from '@chainlit/react-client';
import { useRecoilState } from 'recoil';

import { Translator } from '@/components/i18n';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

import api from '@/api/api';
import { EditSquare } from '../icons/EditSquare';
import { CATEGORIES_ENDPOINT } from '@/lib/constants';

const fetchCategoriesApi = async (userId: string): Promise<string[]> => {
  const data = await api.get<{ categories: string[] }>(`${CATEGORIES_ENDPOINT}?user_id=${userId}`);
  return (data.categories || [])
    .map((c) => (c ?? '').toString().trim())
    .filter((c) => c.length > 0);
};

const saveCategoryApi = async (name: string, userId: string) => {
  await api.post(CATEGORIES_ENDPOINT, { name, user_id: userId });
};

type NewChatDialogProps = {
  open: boolean;
  handleClose: () => void;
  handleConfirm: (payload?: { category?: string; personality?: string }) => void;
  categories?: string[];
  loading?: boolean;
  personalityOptions?: Array<{ label: string; value: string }>;
  selectedPersonality?: string;
  onPersonalityChange?: (value: string) => void;
};

export const NewChatDialog = ({
  open,
  handleClose,
  handleConfirm,
  categories = [],
  loading = false,
  personalityOptions = [],
  selectedPersonality,
  onPersonalityChange,
}: NewChatDialogProps) => {
  const [category, setCategory] = useState<string>('');
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setCategory('');
      setCategoryOpen(false);
      setSearchValue('');
      setError(null);
    }
  }, [open]);

  // Category is required for creating a new chat in this flow.
  // Category is optional — user can start a chat without selecting one.
  const requiresCategory = false;
  const canConfirm = true;

  const filteredCategories = categories.filter(cat =>
    cat.toLowerCase().includes(searchValue.toLowerCase())
  );

  const trimmedSearchValue = searchValue.trim();
  const exactCategoryMatch = useMemo(() => {
    if (!trimmedSearchValue) return null;
    return (
      categories.find((cat) => cat.toLowerCase() === trimmedSearchValue.toLowerCase()) ||
      null
    );
  }, [categories, trimmedSearchValue]);

  const handleCommandInputChange = (value: string) => {
    setSearchValue(value);
    setCategory(value.trim());
    if (error) setError(null);
  };

  const handleSelectCategory = (selectedCategory: string) => {
    const trimmed = selectedCategory.trim();
    setCategory(trimmed);
    setSearchValue(trimmed);
    setCategoryOpen(false);
    if (error) setError(null);
  };

  const handleCommandKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    if (!trimmedSearchValue) return;
    // Cmdk selects the highlighted item on Enter by default; we want Enter to
    // commit the typed text so users can create e.g. "last" even if "last week" exists.
    e.preventDefault();
    e.stopPropagation();
    setCategory(trimmedSearchValue);
    setSearchValue(trimmedSearchValue);
    setCategoryOpen(false);
    if (error) setError(null);
  };

  const onConfirm = () => {
    setCategoryOpen(false);
    const trimmed = category.trim();
    if (requiresCategory && !trimmed) return;

    if (requiresCategory) {
      const exactExists = categories.includes(trimmed);
      const caseMatch = categories.find(
        (cat) => cat.toLowerCase() === trimmed.toLowerCase()
      );

      if (caseMatch && !exactExists) {
        setError(`Category "${caseMatch}" already exists.`);
        return;
      }
    }

    handleConfirm({
      category: trimmed || undefined,
      personality: selectedPersonality || undefined,
    });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent id="new-chat-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            <Translator path="navigation.newChat.dialog.title" />
          </DialogTitle>
          <DialogDescription>
            <Translator path="navigation.newChat.dialog.description" />
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="mb-5">
            <Label className="mb-2 block">Personality / Skill</Label>
            <Select
              value={selectedPersonality || ''}
              onValueChange={(v: string) => onPersonalityChange?.(v)}
              disabled={loading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Select personality / skill..." />
              </SelectTrigger>
              <SelectContent>
                {personalityOptions.map((opt) => (
                  <SelectItem
                    key={opt.value}
                    value={opt.value}
                    className="cursor-pointer"
                  >
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <>
            <Label className="mb-2 block">
              Category <span className="text-muted-foreground text-xs">(optional)</span>
            </Label>

            <Popover
              open={categoryOpen}
              onOpenChange={(open: boolean) => {
                setCategoryOpen(open);
                if (open) {
                  setCategory('');
                  setSearchValue('');
                  setError(null);
                }
              }}
            >
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={categoryOpen}
                  className="w-full justify-between"
                  disabled={loading}
                >
                  <span className="truncate">
                    {category || 'Select or type category...'}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>

              <PopoverContent
                className="p-0 z-[100] w-[var(--radix-popover-trigger-width)] min-w-[var(--radix-popover-trigger-width)]"
                align="start"
                onPointerDown={(e: React.PointerEvent) => e.stopPropagation()}
              >
                <Command shouldFilter={false} className="w-full">
                  <CommandInput
                    placeholder="Search or create category..."
                    value={searchValue}
                    onValueChange={handleCommandInputChange}
                    onKeyDownCapture={handleCommandKeyDown}
                    className="w-full"
                  />

                  <CommandList
                    className="max-h-[240px] overflow-y-auto w-full"
                    onWheel={(e: React.WheelEvent) => {
                      e.stopPropagation();
                    }}
                  >
                    {trimmedSearchValue && !exactCategoryMatch ? (
                      <CommandGroup>
                        <CommandItem
                          value={trimmedSearchValue}
                          onSelect={() => handleSelectCategory(trimmedSearchValue)}
                          className="cursor-pointer"
                        >
                          <Plus className="mr-2 h-4 w-4" />
                          Create "{trimmedSearchValue}"
                        </CommandItem>
                      </CommandGroup>
                    ) : null}

                    <CommandGroup>
                      {filteredCategories.map(cat => (
                        <CommandItem
                          key={cat}
                          value={cat}
                          onSelect={() => handleSelectCategory(cat)}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              category === cat ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          {cat}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>

            {error && (
              <p className="mt-2 text-sm font-medium text-destructive">
                {error}
              </p>
            )}

            {category && !categories.includes(category) && !error && (
              <p className="mt-2 text-sm text-muted-foreground">
                New category will be created: "{category}"
              </p>
            )}
          </>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            <Translator path="common.actions.cancel" />
          </Button>

          <Button
            variant="default"
            onClick={onConfirm}
            disabled={!canConfirm}
          >
            <Translator path="common.actions.confirm" />
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  navigate?: (to: string, options?: { replace?: boolean; state?: any }) => void;
}

const NewChatButton = ({ navigate, ...buttonProps }: Props) => {
  const [open, setOpen] = useState(false);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const { clear, updateChatSettings } = useChatInteract();
  const { user } = useAuth();
  const { threadId } = useChatMessages();
  const { chatSettingsInputs } = useChatData();
  const [chatSettingsValue, setChatSettingsValue] = useRecoilState(
    chatSettingsValueState
  );

  const personalityOptions = useMemo(() => {
    const input = (chatSettingsInputs || []).find(
      (i: any) => i?.id === 'personality_selector'
    );

    const items: Array<{ label: string; value: string }> = (input?.items || [])
      .map((it: any) => ({
        label: (it?.label ?? it?.value ?? '').toString(),
        value: (it?.value ?? it?.label ?? '').toString(),
      }))
      .filter((it: any) => it.value);

    // Always include an Executive Assistant fallback
    if (!items.some((it) => it.value === 'Executive Assistant')) {
      items.unshift({ label: 'Executive Assistant', value: 'Executive Assistant' });
    }

    // De-dupe by value (preserve order)
    const seen = new Set<string>();
    return items.filter((it) => {
      if (seen.has(it.value)) return false;
      seen.add(it.value);
      return true;
    });
  }, [chatSettingsInputs]);

  const selectedPersonality = useMemo(() => {
    const current = chatSettingsValue?.personality_selector;
    if (typeof current === 'string' && current.trim()) return current;
    const executive = personalityOptions.find((o) => o.value === 'Executive Assistant');
    return executive?.value || personalityOptions[0]?.value || 'Executive Assistant';
  }, [chatSettingsValue?.personality_selector, personalityOptions]);

  useEffect(() => {
    if (open && user?.id) fetchCategories();
  }, [open, user?.id]);

  const fetchCategories = async () => {
    if (!user?.id) return;
    setLoading(true);

    try {
      const cats = await fetchCategoriesApi(user.id);
      // Ensure the UI never renders an "empty" category option.
      setCategories(
        (cats || []).map((c) => c.trim()).filter((c) => c.length > 0)
      );
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (payload?: {
    category?: string;
    personality?: string;
  }) => {
    if (!user?.id) return;
    const trimmedCategory = payload?.category?.trim() || '';

    try {
      // Close dialog immediately to feel responsive
      setOpen(false);

      const personalityToSet =
        payload?.personality?.trim() || selectedPersonality || 'Executive Assistant';

      // Keep Settings panel + backend in sync (single source of truth).
      setChatSettingsValue((prev: any) => ({
        ...(prev || {}),
        personality_selector: personalityToSet,
      }));
      updateChatSettings({ personality_selector: personalityToSet });

      if (trimmedCategory) {
        const exactExists = categories.includes(trimmedCategory);
        if (!exactExists) {
          await saveCategoryApi(trimmedCategory, user.id);
        }
      }

      sessionStorage.setItem('ryze_pending_category', trimmedCategory);
      sessionStorage.setItem('ryze_is_new_chat', 'true');

      if (threadId) {
        sessionStorage.setItem('ryze_ignored_thread_id', threadId);
      }

      // Clear the chat session to start fresh.
      clear();

      // Navigate to the home page without a full page reload.
      if (navigate) {
        navigate('/');
      }
    } catch (err) {
      console.error('Ryze: Error in handleConfirm:', err);
    }
  };

  return (
    <div>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              id="new-chat-button"
              className="text-muted-foreground hover:text-muted-foreground"
              onClick={() => setOpen(true)}
              {...buttonProps}
            >
              <EditSquare className="!size-6" />
            </Button>
          </TooltipTrigger>

          <TooltipContent>
            <Translator path="navigation.newChat.dialog.tooltip" />
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <NewChatDialog
        open={open}
        handleClose={() => setOpen(false)}
        handleConfirm={handleConfirm}
        categories={categories}
        loading={loading}
        personalityOptions={personalityOptions}
        selectedPersonality={selectedPersonality}
        onPersonalityChange={(value) => {
          setChatSettingsValue((prev: any) => ({
            ...(prev || {}),
            personality_selector: value,
          }));
          updateChatSettings({ personality_selector: value });
        }}
      />
    </div>
  );
};

export default NewChatButton;